import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useBranding } from '../context/BrandingContext';
import '../styles/footer.css';

// Marks the first item of every visual row of the footer nav with
// `data-row-start`, which hides that item's separator (footer.css). The
// separator is absolutely positioned, so toggling it never moves an item —
// there is no layout feedback loop. Exported for the pin.
export function markFooterRowStarts(list) {
  if (!list) return;
  let prevTop = null;
  Array.from(list.children).forEach((item) => {
    const top = item.offsetTop;
    const starts = prevTop === null || top > prevTop + 1;
    if (starts) item.setAttribute('data-row-start', '');
    else item.removeAttribute('data-row-start');
    prevTop = top;
  });
}

const Footer = () => {
  const { getTemplateAsset, template } = useBranding();
  const navRef = useRef(null);

  const links = [
    { label: 'Shop Frogs', to: '/frogs' },
    { label: 'Shop Geckos', to: '/geckos' },
    { label: 'Shop Plants', to: '/plants' },
    { label: 'Shipping Policy', to: '/shipping-policy' },
    { label: 'Care Guides', to: '/care-guides' },
    { label: 'About Us', to: '/about' },
    {
      label: 'Support',
      href: template?.footerLinks?.find(link => link.label === 'Support')?.href || 'mailto:support@feelingfroggy.llc',
    },
    { label: 'FAQ', to: '/faq' },
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms of Service', to: '/terms' },
    { label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=100094008310076', external: true },
    { label: 'MorphMarket', href: 'https://morphmarket.com/stores/allenlucke/', external: true },
  ];

  useLayoutEffect(() => {
    const list = navRef.current;
    if (!list) return undefined;
    const update = () => markFooterRowStarts(list);
    update();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(list);
    }
    window.addEventListener('resize', update);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update).catch(() => {});
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <footer className="ff-footer">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            {/* Navigation Links. Batch 34 review NIT: a separator never
                LEADS or TRAILS a row at any width. Each "•" is the item's
                own ::before, absolutely positioned in the gap to its left
                (footer.css), so it adds no width and showing or hiding it
                cannot re-flow the row; `markFooterRowStarts` hides the one
                on the first item of every wrapped row. */}
            <ul className="ff-footer-nav" ref={navRef}>
              {links.map(({ label, to, href, external }) => (
                <li key={label} className="ff-footer-item">
                  {to ? (
                    <Link className="ff-footer-link" to={to}>{label}</Link>
                  ) : (
                    <a
                      href={href}
                      className="ff-footer-link"
                      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {label}
                    </a>
                  )}
                </li>
              ))}
            </ul>

            {/* Footer Bottom */}
            <div className="ff-footer-bottom">
              <div className="footer-motto footer-tagline">
                <img
                  src="/logos/ff original 500px.png"
                  alt="Feeling Froggy logo"
                  className="ff-footer-logo"
                  height="18"
                />
                <span>Are you feeling froggy…</span>
                {/* Trailing logo is decoratively duplicated; first logo
                    carries the alt text. Hidden on mobile via a CSS rule
                    in footer.css to avoid the vertical-sandwich effect
                    when .footer-motto flips to column at <md. Can't use
                    Bootstrap .d-none here — brand-overrides.css forces
                    .footer-motto img visible with !important. */}
                <img
                  src="/logos/ff original 500px.png"
                  alt=""
                  aria-hidden="true"
                  className="ff-footer-logo ff-footer-logo--decorative"
                  height="18"
                />
              </div>
              <p className="ff-footer-copyright">
                © {new Date().getFullYear()} Feeling Froggy LLC
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export default Footer;